'use client';

import type { MobileMenuProps } from '@/layout/navbar/navbar';
import { SearchButton } from '@/layout/navbar/search-button';
import { SearchButtonComponent } from '@/layout/navbar/search-button-component';
import { WishlistButton } from '@/layout/navbar/wishlist-button';
import { MobileMenuShared } from '@veranda/shared/layout/navbar';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import { type CSSProperties, type ReactNode, Suspense } from 'react';
import { AccountButton } from './account-button';

const triggerClassName = 'uppercase font-medium';

export function MobileMenu({
  data,
  children,
}: { data: MobileMenuProps; children: ReactNode }) {
  const pathname = usePathname();
  if (!data) return null;

  return (
    <MobileMenuShared
      key={pathname}
      style={
        {
          '--color': 'hsl(var(--color-brand-burgundy))',
        } as CSSProperties
      }
      className="flex h-[var(--header-height)] items-center justify-between"
    >
      <MobileMenuShared.Trigger className="flex self-center" />
      <MobileMenuShared.Content
        title="Menu"
        description="Menu items for the mobile menu"
        className="bg-brand-beige"
      >
        {data.map((item) => {
          if (item.itemType === 'mobileMegaMenu') {
            return (
              <MobileMenuShared.SubItem key={item._key}>
                <MobileMenuShared.SubmenuTrigger
                  colSpan={item.columnCount ?? 2}
                  style={{
                    backgroundColor: item.settings?.bgColor ?? undefined,
                    color: item.settings?.textColor ?? undefined,
                  }}
                  images={item.settings?.images}
                  className={triggerClassName}
                >
                  {item.title}
                </MobileMenuShared.SubmenuTrigger>

                <MobileMenuShared.SubmenuContent
                  title={item.title}
                  description={`Submenu for ${item.title}`}
                  className="bg-brand-beige"
                >
                  {item.links.map((subitem) => (
                    <MobileMenuShared.Item
                      key={subitem._key}
                      href={subitem.link.slug || ''}
                      target={subitem.link.openInNewTab ? '_blank' : '_self'}
                      colSpan={2}
                      style={{
                        backgroundColor: subitem.settings?.bgColor ?? undefined,
                        color: subitem.settings?.textColor ?? undefined,
                      }}
                      images={subitem.settings?.images}
                      className={triggerClassName}
                    >
                      {subitem.link.text}
                    </MobileMenuShared.Item>
                  ))}
                </MobileMenuShared.SubmenuContent>
              </MobileMenuShared.SubItem>
            );
          }

          return (
            <MobileMenuShared.Item
              key={item._key}
              colSpan={item.columnCount ?? 2}
              href={item.link.slug || ''}
              target={item.link.openInNewTab ? '_blank' : '_self'}
              style={{
                backgroundColor: item.settings?.bgColor ?? undefined,
                color: item.settings?.textColor ?? undefined,
              }}
            >
              {item.link.text}
            </MobileMenuShared.Item>
          );
        })}
      </MobileMenuShared.Content>
      <MobileMenuShared.Link href="/">
        <Image
          src="/images/logo.webp"
          alt="Logo"
          width={354}
          height={116}
          className="h-auto max-h-[48px] w-auto max-w-full"
        />
      </MobileMenuShared.Link>
      <div className="flex items-center">
        <AccountButton />
        <WishlistButton />
        {children}
        <Suspense
          fallback={
            <SearchButtonComponent
              isOpen={false}
              // biome-ignore lint/suspicious/noEmptyBlockStatements: <explanation>
              setIsOpen={() => {}}
              className="pr-0"
            />
          }
        >
          <SearchButton className="pr-0" />
        </Suspense>
      </div>
    </MobileMenuShared>
  );
}
